import React, { useCallback, useEffect, useState } from "react";
import { Moon, RefreshCw, Check, PauseCircle, Activity } from "lucide-react";
import * as api from "../../api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("es-VE", { timeZone: "America/Caracas", hour12: false }) : "—");

/** Cierre automático nocturno por inactividad del admin (America/Caracas). */
export function AutoCloseCard({ showToast, onSiteStatusChanged }) {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const s = await api.adminAutoCloseStatus();
      setStatus(s);
      setForm((f) => f || { ...s.config });
    } catch (e) { console.warn("auto-close status failed:", e); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const save = async (patch) => {
    setBusy(true);
    try {
      const s = await api.adminAutoCloseUpdate(patch);
      setStatus(s); setForm({ ...s.config });
      showToast("Cierre automático", patch.enabled === undefined ? "Configuración guardada." : (patch.enabled ? "Activado." : "Desactivado."), "success");
      onSiteStatusChanged?.();
    } catch (err) { showToast("Error", err.message || "No se pudo guardar", "error"); }
    finally { setBusy(false); }
  };

  const exception = async (minutes) => {
    setBusy(true);
    try {
      const s = await api.adminAutoCloseException(minutes);
      setStatus(s);
      showToast("Excepción temporal", minutes ? `El cierre automático no actuará durante ${minutes / 60} h.` : "Excepción retirada.", "success");
    } catch (err) { showToast("Error", err.message || "No se pudo aplicar", "error"); }
    finally { setBusy(false); }
  };

  if (!status || !form) return null;
  const cfg = status.config;
  const autoClosed = status.site && !status.site.open && status.site.closedBy === "auto_inactivity";
  const input = "w-full bg-ag7-bg border border-[var(--ag7-border-soft)] rounded-xl px-3 py-2.5 text-white text-sm font-mono focus:outline-none focus:border-indigo-400";

  return (
    <div className="rounded-3xl bg-ag7-surface border border-[var(--ag7-border-soft)] p-8 space-y-5" data-testid="admin-auto-close-card">
      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-full border flex items-center justify-center flex-shrink-0 ${cfg.enabled ? "bg-indigo-950/60 border-indigo-400" : "bg-zinc-900 border-zinc-600"}`}>
          <Moon className={`w-5 h-5 ${cfg.enabled ? "text-indigo-300" : "text-zinc-500"}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-xl font-heading font-extrabold tracking-tight text-white">Cierre automático nocturno</h3>
            <span data-testid="auto-close-enabled-badge" className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${cfg.enabled ? "bg-indigo-950/60 border-indigo-400 text-indigo-200" : "bg-zinc-900 border-zinc-600 text-zinc-400"}`}>
              {cfg.enabled ? "ACTIVO" : "INACTIVO"}
            </span>
            {status.in_window && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border bg-amber-950/50 border-amber-500/60 text-amber-200">EN HORARIO NOCTURNO</span>}
          </div>
          <p className="text-[11px] text-zinc-400 mt-1">
            Si el administrador no interactúa con el panel durante los minutos indicados dentro del horario (America/Caracas),
            la tienda se cierra sola y se bloquean pedidos nuevos. Los pedidos y pagos existentes no se tocan.
          </p>
        </div>
      </div>

      {autoClosed && (
        <div data-testid="auto-close-closed-banner" className="rounded-xl border border-red-500/50 bg-red-950/40 p-3 text-xs text-red-100">
          🌙 La tienda fue <strong>cerrada automáticamente por inactividad</strong> ({fmt(status.site.closedAt)}). Ábrela desde el interruptor de arriba cuando retomes.
        </div>
      )}

      <button type="button" disabled={busy} onClick={() => save({ enabled: !cfg.enabled })} data-testid="auto-close-toggle-btn"
        className={`w-full h-12 rounded-2xl border-2 font-bold text-sm flex items-center justify-center gap-2 transition-colors ${cfg.enabled ? "border-indigo-400 bg-indigo-500/15 text-indigo-100 hover:bg-indigo-500/25" : "border-zinc-600 bg-zinc-900 text-zinc-300 hover:border-indigo-400"}`}>
        {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Moon className="w-4 h-4" />}
        {cfg.enabled ? "Cierre automático ACTIVO — pulsa para desactivar" : "Cierre automático INACTIVO — pulsa para activar"}
      </button>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <label className="space-y-1">
          <span className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500">Desde (Caracas)</span>
          <input type="time" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className={input} data-testid="auto-close-start-input" />
        </label>
        <label className="space-y-1">
          <span className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500">Hasta (Caracas)</span>
          <input type="time" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className={input} data-testid="auto-close-end-input" />
        </label>
        <label className="space-y-1 col-span-2 sm:col-span-1">
          <span className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500">Minutos de inactividad</span>
          <input type="number" min={5} max={720} value={form.inactivity_minutes}
            onChange={(e) => setForm({ ...form, inactivity_minutes: e.target.value })} className={input} data-testid="auto-close-minutes-input" />
        </label>
      </div>
      <label className="block space-y-1">
        <span className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500">Mensaje para los clientes al cerrar</span>
        <textarea rows={2} maxLength={500} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
          className={`${input} resize-none`} data-testid="auto-close-reason-input" />
      </label>
      <button type="button" disabled={busy} data-testid="auto-close-save-btn"
        onClick={() => save({ start: form.start, end: form.end, inactivity_minutes: Number(form.inactivity_minutes), reason: form.reason })}
        className="w-full bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white font-bold py-3 rounded-xl text-xs flex items-center justify-center gap-2">
        {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Guardar configuración
      </button>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px] font-mono bg-zinc-950 p-3 rounded-xl border border-[var(--ag7-border-soft)]" data-testid="auto-close-status">
        <div className="flex items-center gap-2 text-zinc-300"><Activity className="w-3.5 h-3.5 text-emerald-400" />
          Última actividad: <strong className="text-white" data-testid="auto-close-last-activity">{fmt(status.last_activity_at)}</strong>
          {status.idle_minutes != null && <span className="text-zinc-500">({Math.round(status.idle_minutes)} min)</span>}
        </div>
        <div className="text-zinc-300">Cerraría a las: <strong className="text-white" data-testid="auto-close-would-close-at">{status.would_close_at ? fmt(status.would_close_at) : "—"}</strong></div>
        <div className="text-zinc-300">Último cierre automático: <strong className="text-white">{fmt(status.last_auto_close_at)}</strong></div>
        <div className="text-zinc-300">Excepción hasta: <strong className={status.exempt_until ? "text-amber-300" : "text-white"} data-testid="auto-close-exempt-until">{status.exempt_until ? fmt(status.exempt_until) : "—"}</strong></div>
      </div>

      <div className="flex flex-wrap gap-2">
        {[1, 3, 8].map((h) => (
          <button key={h} type="button" disabled={busy} onClick={() => exception(h * 60)} data-testid={`auto-close-exception-${h}h-btn`}
            className="flex-1 min-w-[30%] px-3 py-2.5 rounded-xl border border-amber-500/50 bg-amber-950/30 hover:bg-amber-900/40 text-amber-100 text-xs font-bold inline-flex items-center justify-center gap-1.5">
            <PauseCircle className="w-3.5 h-3.5" /> Pausar {h} h
          </button>
        ))}
        {status.exempt_until && (
          <button type="button" disabled={busy} onClick={() => exception(0)} data-testid="auto-close-exception-clear-btn"
            className="w-full px-3 py-2 rounded-xl border border-zinc-600 text-zinc-300 hover:text-white text-xs font-bold">
            Quitar excepción
          </button>
        )}
      </div>
    </div>
  );
}
